/* English dictionary — the fallback locale. Identical key set to tr.ts;
   i18n.test.ts fails the build if the two ever diverge. */

import type { Dict } from './index.js'

export const dict: Dict = {
  // ── common ──
  "common.save": "Save",
  "common.cancel": "Cancel",
  "common.delete": "Delete",
  "common.edit": "Edit",
  "common.add": "Add",
  "common.search": "Search",
  "common.filter": "Filter",
  "common.clearFilters": "Clear filters",
  "common.loadMore": "Load more",
  "common.retry": "Retry",
  "common.copy": "Copy",
  "common.copied": "Copied",
  "common.confirm": "Confirm",
  "common.close": "Close",
  "common.back": "Back",
  "common.next": "Next",
  "common.details": "Details",
  "common.reason": "Reason",
  "common.never": "Never",
  "common.permanent": "Permanent",
  "common.all": "All",
  "common.none": "None",
  "common.yes": "Yes",
  "common.no": "No",
  "common.loading": "Loading…",
  "common.download": "Download",
  "common.export": "Export",
  "common.import": "Import",
  "common.unsaved": "{n} unsaved changes",
  "common.noPermission": "You do not have permission to do this",
  "common.permissionDenied": "You do not have access to this page",
  "common.serverOffline": "The server is offline",
  "common.emptyFiltered": "No results with these filters",
  "common.severity.low": "Low",
  "common.severity.medium": "Medium",
  "common.severity.high": "High",
  "common.severity.critical": "Critical",
  "common.action.log": "Log",
  "common.action.kick": "Kick",
  "common.action.ban": "Ban",
  "common.action.downgraded": "{from} → {to} (server did not confirm)",
  "common.side.client": "Client",
  "common.side.server": "Server",
  "common.side.cloud": "Cloud",
  "common.status.never": "Never connected",
  "common.status.online": "Online",
  "common.status.offline": "Offline",
  "common.role.owner": "Owner",
  "common.role.admin": "Admin",
  "common.role.moderator": "Moderator",
  "common.role.viewer": "Viewer",
  "common.appName": "SentinelAC",
  "common.optional": "optional",
  "common.required": "required",
  "common.remove": "Remove",
  "common.clear": "Clear",
  "common.expand": "Expand",
  "common.collapse": "Collapse",
  "common.selectPlaceholder": "Select…",
  "common.searchPlaceholder": "Search…",
  "common.noResults": "No results",
  "common.errorTitle": "Something went wrong",
  "common.loadedCount": "{n} rows loaded",
  "common.openMenu": "Open menu",
  "common.userMenu": "User menu",
  "common.theme": "Theme",
  "common.themeDark": "Dark",
  "common.themeLight": "Light",
  "common.themeSystem": "System",
  "common.language": "Language",
  "common.locale.tr": "Türkçe",
  "common.locale.en": "English",
  "common.commandPalette": "Command palette",
  "common.notFoundTitle": "Page not found",
  "common.notFoundDesc": "The page you were looking for has moved or never existed.",
  "common.goHome": "Back to my servers",
  "common.skipToContent": "Skip to content",
  "common.requestId": "Request id",
  "common.step": "Step {n} of {total}",
  "common.showMore": "Show more",
  "common.showLess": "Show less",
  "common.copyFailed": "Could not copy",
  "common.reveal": "Reveal",
  "common.hide": "Hide",
  "common.typeToConfirm": "Type {text} to confirm",

  // ── nav ──
  "nav.dashboard": "Dashboard",
  "nav.live": "Live Feed",
  "nav.players": "Players",
  "nav.detections": "Detections",
  "nav.bans": "Bans",
  "nav.globalBans": "Global Bans",
  "nav.whitelist": "Whitelist",
  "nav.screenshots": "Screenshots",
  "nav.config": "Configuration",
  "nav.commands": "Commands",
  "nav.discord": "Discord",
  "nav.team": "Team",
  "nav.licenses": "Licenses",
  "nav.audit": "Audit",
  "nav.settings": "Settings",
  "nav.servers": "Servers",
  "nav.lookup": "Identifier Lookup",
  "nav.account": "Account",
  "nav.apiKeys": "API Keys",
  "nav.adminGlobalBans": "Global Ban Review",
  "nav.logout": "Log out",
  "nav.pages": "Pages",
  "nav.groupGeneral": "General",
  "nav.groupPlayers": "Players",
  "nav.groupManage": "Management",
  "nav.sidebar": "Sidebar",
  "nav.mainNav": "Main navigation",

  // ── auth ──
  "auth.loginTitle": "Sign in",
  "auth.registerTitle": "Create an account",
  "auth.forgotTitle": "Forgot your password",
  "auth.resetTitle": "Set a new password",
  "auth.inviteTitle": "Invitation",
  "auth.email": "Email",
  "auth.password": "Password",
  "auth.passwordConfirm": "Password (again)",
  "auth.displayName": "Display name",
  "auth.currentPassword": "Current password",
  "auth.newPassword": "New password",
  "auth.login": "Sign in",
  "auth.register": "Sign up",
  "auth.forgotLink": "Forgot your password?",
  "auth.registerLink": "No account yet? Sign up",
  "auth.loginLink": "Back to sign in",
  "auth.passwordRule": "At least 10 characters",
  "auth.forgotSent": "If an account exists, an email has been sent",
  "auth.resetDone": "Your password has been updated",
  "auth.sessionsRevoked": "All other sessions have been signed out",
  "auth.inviteAccept": "Accept invitation",
  "auth.inviteBody": "You were invited to {server} as {role}",
  "auth.send": "Send link",
  "auth.passwordMismatch": "Passwords do not match",
  "auth.strength": "Password strength",
  "auth.strength.weak": "Weak",
  "auth.strength.fair": "Fair",
  "auth.strength.good": "Good",
  "auth.strength.strong": "Strong",
  "auth.registrationClosedTitle": "Registration is closed",
  "auth.registrationClosedDesc": "You need an invitation link to create an account.",
  "auth.lockedUntil": "Account locked until {lockedUntil}",
  "auth.inviteExpired": "This invitation is invalid or has expired",
  "auth.acceptedTitle": "Invitation accepted",
  "auth.inviteIntro": "Accepting the invitation adds the server to your account and gives you the role its owner chose.",
  "auth.forgotIntro": "Enter your email address and we will send you a link to set a new password.",
  "auth.resetIntro": "Choose a new password. Every open session will be signed out.",
  "auth.signedInAs": "Signed in as {email}",

  // ── server ──
  "server.title": "Servers",
  "server.new": "New server",
  "server.name": "Server name",
  "server.framework": "Framework",
  "server.timezone": "Time zone",
  "server.emptyTitle": "You have no servers yet",
  "server.emptyDesc": "Create your first server and get its license key",
  "server.playersRatio": "{online}/{max} players",
  "server.detections24h": "Detections (24h)",
  "server.bans24h": "Bans (24h)",
  "server.onlineSummary": "Online · {players}/{max} · v{agentVersion} · config v{configVersion}",
  "server.offlineSince": "Offline · last heartbeat {age}",
  "server.outdated": "Resource version is outdated ({agentVersion} → {minAgentVersion})",
  "server.outdatedBadge": "outdated",
  "server.ipLock": "IP lock: {ip}",
  "server.resetIp": "Reset IP",
  "server.reload": "Reload",
  "server.reconnect": "Reconnect",
  "server.wsOpen": "Live",
  "server.wsReconnecting": "Reconnecting…",
  "server.wsClosed": "Disconnected",
  "server.wizardStep1": "Server details",
  "server.wizardStep2": "License key",
  "server.wizardStep3": "Installation",
  "server.keyOnce": "This key will never be shown again",
  "server.keySaved": "I have saved the key",
  "server.setrWarning": "Always define the key with set; setr replicates the convar to every client",
  "server.downloadResource": "Download the resource",
  "server.serverCfg": "server.cfg lines",
  "server.hardening": "Recommended hardening settings",
  "server.waitingHeartbeat": "Waiting for the first heartbeat…",
  "server.connected": "Server connected!",
  "server.goToPanel": "Go to the dashboard",
  "server.switcher": "Switch server",
  "server.created": "Server created",
  "server.wizardTitle": "Create a new server",
  "server.leaveWarning": "If you leave before saving the license key you will never see it again.",
  "server.installSteps": "Installation steps",
  "server.apiUrl": "API address",
  "server.resourceVersion": "Resource version",
  "server.nameTaken": "A server with this name already exists",
  "server.heartbeatHint": "Start the server; this step advances by itself when the first heartbeat lands.",
  "server.framework.standalone": "Standalone (no framework)",
  "server.framework.esx": "ESX",
  "server.framework.qbcore": "QBCore",
  "server.framework.qbox": "Qbox",
  "server.framework.vrp": "vRP",
  "server.copyBlock": "Copy this block",
  "server.wizardCancel": "Cancel and go back to my servers",
  "server.keyOnceDesc": "Store it in your password manager now. There is no way to read it again; a lost key can only be rotated.",
  "server.configSynced": "Config v{version}",

  // ── dashboard ──
  "dashboard.title": "Dashboard",
  "dashboard.playersOnline": "Players online",
  "dashboard.detections24h": "Detections (24h)",
  "dashboard.bans24h": "Bans (24h)",
  "dashboard.bansTotal": "Total bans",
  "dashboard.detectionsOverTime": "Detections over time",
  "dashboard.playersOverTime": "Players over time",
  "dashboard.topDetections": "Top detections",
  "dashboard.recentBans": "Recent bans",
  "dashboard.recentDetections": "Recent detections",
  "dashboard.agentHealth": "Resource health",
  "dashboard.uptime": "Uptime",
  "dashboard.tickMs": "Tick time",
  "dashboard.latency": "Latency",
  "dashboard.queueDepth": "Queue",
  "dashboard.noData": "No data yet",
  "dashboard.range24h": "24 hours",
  "dashboard.range7d": "7 days",
  "dashboard.range30d": "30 days",

  // ── detection ──
  "detection.title": "Detections",
  "detection.detail": "Detection detail",
  "detection.liveTitle": "Live feed",
  "detection.occurredAt": "Occurred",
  "detection.receivedAt": "Received",
  "detection.serverConfirmed": "Server confirmed",
  "detection.score": "Evidence weight",
  "detection.strikes": "Strikes",
  "detection.samples": "Samples",
  "detection.evidence": "Evidence",
  "detection.raw": "Raw payload",
  "detection.coords": "Position",
  "detection.markFalsePositive": "Mark as false positive",
  "detection.reviewNote": "Review note",
  "detection.bulkReview": "Review selected",
  "detection.pause": "Pause",
  "detection.resume": "Resume",
  "detection.autoScroll": "Auto-scroll",
  "detection.clear": "Clear",
  "detection.buffered": "{n} new",
  "detection.newRows": "New rows available",
  "detection.byType": "By type",
  "detection.byAction": "By action",
  "detection.emptyTitle": "No detections with these filters",
  "detection.feedEmptyTitle": "The feed is empty",
  "detection.feedEmptyDesc": "Your modules may all be in log-only mode",
  "detection.offlineBanner": "The server is offline; no new detections will arrive",

  // ── player ──
  "player.title": "Players",
  "player.lookupTitle": "Identifier lookup",
  "player.tabOnline": "Online",
  "player.tabAll": "All",
  "player.tabRecent": "Recently left",
  "player.tabDetections": "Detections",
  "player.tabBans": "Bans",
  "player.tabScreenshots": "Screenshots",
  "player.tabSessions": "Sessions",
  "player.tabOtherServers": "Other servers",
  "player.trustScore": "Trust score",
  "player.playtime": "Playtime",
  "player.joinCount": "Join count",
  "player.firstSeen": "First seen",
  "player.lastSeen": "Last seen",
  "player.notes": "Notes",
  "player.notesSaved": "Note saved",
  "player.tokens": "Tokens ({n})",
  "player.dropReason": "Drop reason",
  "player.requestScreenshot": "Request screenshot",
  "player.sendMessage": "Send message",
  "player.kick": "Kick",
  "player.emptyOnline": "Nobody is connected right now",
  "player.emptyAll": "No player records yet",
  "player.lookupPlaceholder": "license:… · discord:… · steam:…",
  "player.lookupEmpty": "This identifier has not been seen on any of your servers",
  "player.lookupMatches": "{n} matches",

  // ── ban ──
  "ban.title": "Bans",
  "ban.new": "New ban",
  "ban.detail": "Ban detail",
  "ban.tabActive": "Active",
  "ban.tabExpired": "Expired",
  "ban.tabRevoked": "Revoked",
  "ban.tabGlobal": "Global",
  "ban.banCode": "Ban code",
  "ban.provisional": "Provisional code",
  "ban.issuedBy": "Issued by",
  "ban.expires": "Expires",
  "ban.scope": "Scope",
  "ban.target": "Target",
  "ban.pasteIdentifiers": "Paste identifiers (one per line)",
  "ban.duration": "Duration",
  "ban.kickNow": "Kick immediately if the player is online",
  "ban.revoke": "Revoke ban",
  "ban.revokeReason": "Revocation reason",
  "ban.changeExpiry": "Change expiry",
  "ban.importTitle": "Import ban list",
  "ban.importResult": "{imported} imported, {skipped} skipped",
  "ban.alreadyBanned": "This identifier is already banned",
  "ban.presetCheatMenu": "Cheat menu",
  "ban.presetAimbot": "Aimbot",
  "ban.presetGodmode": "Godmode",
  "ban.presetSpawnAbuse": "Spawn abuse",
  "ban.presetEvasion": "Ban evasion",
  "ban.presetOther": "Other",
  "ban.emptyTitle": "No bans yet",
  "ban.globalTitle": "Global bans",
  "ban.globalExplainer": "The global list shares confirmed bans between participating servers",
  "ban.enforce": "Enforce global bans",
  "ban.contribute": "Propose my bans to the global list",
  "ban.modeBlock": "Block",
  "ban.modeFlag": "Flag only",
  "ban.modeOff": "Off",
  "ban.reputation": "Reputation",
  "ban.eligibility": "Eligibility",
  "ban.corroborations": "Corroborations",
  "ban.reporter": "Reporter",
  "ban.exempt": "Exempt on this server",
  "ban.whyBlocked": "Why was this blocked?",
  "ban.proposeGlobal": "Propose as a global ban",
  "ban.approve": "Approve",
  "ban.reject": "Reject",
  "ban.revokeGlobal": "Revoke the global ban",
  "ban.queueEmpty": "The review queue is empty",
  "ban.global.proposed": "Proposed",
  "ban.global.approved": "Approved",
  "ban.global.rejected": "Rejected",
  "ban.global.revoked": "Revoked",
  "ban.global.none": "Local",

  // ── whitelist ──
  "whitelist.title": "Whitelist",
  "whitelist.add": "Add to whitelist",
  "whitelist.fromPlayer": "Add from player",
  "whitelist.identifierType": "Identifier type",
  "whitelist.identifierValue": "Identifier value",
  "whitelist.label": "Label",
  "whitelist.scope": "Scope",
  "whitelist.scopeAll": "All modules",
  "whitelist.scopeModules": "Selected modules",
  "whitelist.bypassConnect": "Skip the connect ban check",
  "whitelist.bypassConnectOnlyAll": "The connect check can only be skipped when all modules are selected",
  "whitelist.expires": "Expires",
  "whitelist.aceNote": "An ACE permission (sentinel.bypass) is not a whitelist row: ACE is immediate, a whitelist row is durable",
  "whitelist.emptyTitle": "The whitelist is empty",

  // ── config ──
  "config.title": "Configuration",
  "config.version": "Version",
  "config.lastChange": "Last change by {by} · {time}",
  "config.logOnly": "Log-only mode",
  "config.logOnlyBanner": "No module is enforcing kick or ban",
  "config.tabModules": "Modules",
  "config.tabGeneral": "General",
  "config.tabConnect": "Connect and exemptions",
  "config.tabLists": "Lists",
  "config.tabEventRules": "Event rules",
  "config.eventRules.title": "Event rules",
  "config.eventRules.desc": "Write rules over your own net events: name matches, argument conditions and a per-player rate limit.",
  "config.eventRules.note": "Each rule carries its own action. Start at `Log`, watch what it catches in the live feed, then promote it. Only the event names you list are watched; there is no wildcard.",
  "config.eventRules.empty": "No rules yet.",
  "config.eventRules.add": "Add rule",
  "config.eventRules.newName": "New rule",
  "config.eventRules.remove": "Delete",
  "config.eventRules.enabled": "Enabled",
  "config.eventRules.name": "Rule name",
  "config.eventRules.events": "Event names",
  "config.eventRules.mode": "Mode",
  "config.eventRules.modeRate": "Rate limit — when the window is exceeded",
  "config.eventRules.modeMatch": "Match — on the first occurrence",
  "config.eventRules.maxPerWindow": "Max per window",
  "config.eventRules.windowSec": "Window (sec)",
  "config.eventRules.action": "Action",
  "config.eventRules.cancel": "Cancel the event",
  "config.eventRules.conditions": "Conditions — all must hold",
  "config.eventRules.addCondition": "Add condition",
  "config.eventRules.removeCondition": "Remove condition",
  "config.eventRules.arg": "Argument",
  "config.eventRules.op": "Comparison",
  "config.eventRules.value": "Value",
  "config.eventRules.op.equals": "equals",
  "config.eventRules.op.notEquals": "does not equal",
  "config.eventRules.op.contains": "contains",
  "config.eventRules.op.gt": "greater than",
  "config.eventRules.op.lt": "less than",
  "config.eventRules.op.isType": "type is",
  "config.eventRules.op.missing": "is missing",
  "config.enabled": "Enabled",
  "config.action": "Action",
  "config.strikes": "Strike threshold",
  "config.window": "Window (s)",
  "config.screenshot": "Screenshot",
  "config.banDuration": "Ban duration",
  "config.options": "Options",
  "config.lockedAction": "This module can only log",
  "config.history": "History",
  "config.rollback": "Roll back",
  "config.diff": "View diff",
  "config.changeSummary": "Change summary",
  "config.saved": "Config v{version} saved · the server picks it up within 35 s",
  "config.appliedOk": "Applied on the server ✓ v{version}",
  "config.pendingSync": "Config v{applied} → v{current} pending",
  "config.conflictTitle": "The configuration changed",
  "config.conflictBody": "{by} saved v{version}",
  "config.conflictRebase": "Reload and re-apply my changes",
  "config.changedElsewhere": "Another admin saved v{version}",
  "config.resetDefaults": "Reset to defaults",
  "config.importTitle": "Import configuration",
  "config.invalidIssues": "{n} fields are invalid",
  "config.category.player": "Player",
  "config.category.weapon": "Weapon",
  "config.category.vehicle": "Vehicle",
  "config.category.combat": "Combat",
  "config.category.world": "World",
  "config.category.integrity": "Integrity",
  "config.category.network": "Network",
  "config.category.connection": "Connection",

  // ── screenshot ──
  "screenshot.title": "Screenshots",
  "screenshot.pending": "Pending",
  "screenshot.failed": "Screenshot failed ({reason})",
  "screenshot.requested": "Screenshot requested",
  "screenshot.goToDetection": "Go to detection",
  "screenshot.expiresIn": "Deleted in {time}",
  "screenshot.signedUrl": "Shareable link",
  "screenshot.emptyTitle": "No screenshots yet",
  "screenshot.emptyDesc": "The game server needs the screenshot-basic resource",

  // ── command ──
  "command.title": "Commands",
  "command.send": "Send command",
  "command.sent": "Command queued",
  "command.type": "Type",
  "command.broadcast": "Everyone",
  "command.message": "Message",
  "command.type.kick": "Kick",
  "command.type.message": "Message",
  "command.type.screenshot": "Screenshot",
  "command.type.reloadConfig": "Reload configuration",
  "command.status.queued": "Queued",
  "command.status.delivered": "Delivered",
  "command.status.acked": "Acknowledged",
  "command.status.failed": "Failed",
  "command.status.expired": "Expired",
  "command.queueAnyway": "Queue it anyway",
  "command.emptyTitle": "No commands sent yet",

  // ── discord ──
  "discord.title": "Discord",
  "discord.add": "Add webhook",
  "discord.url": "Webhook URL",
  "discord.events": "Events",
  "discord.minSeverity": "Minimum severity",
  "discord.mentionRole": "Role to mention",
  "discord.includeScreenshot": "Include the screenshot",
  "discord.test": "Send a test",
  "discord.testOk": "Test message sent",
  "discord.disabled": "Disabled: {reason}",
  "discord.reenable": "Re-enable",
  "discord.securityNote": "The webhook URL lives only in the cloud and is never sent to the game server",
  "discord.preview": "Preview",
  "discord.emptyTitle": "No webhooks configured",

  // ── team ──
  "team.title": "Team",
  "team.invite": "Invite",
  "team.inviteLink": "Invitation link",
  "team.pending": "Pending invitations",
  "team.transfer": "Transfer ownership",
  "team.remove": "Remove",
  "team.role": "Role",
  "team.joined": "Joined",
  "team.cannotChangeOwner": "The owner's role cannot be changed",
  "team.matrixTitle": "Role permissions",
  "team.emptyInvites": "No pending invitations",

  // ── license ──
  "license.title": "Licenses",
  "license.key": "Key",
  "license.boundIp": "Bound IP",
  "license.bindMode": "Bind mode",
  "license.bindMode.first_use": "Bind on first use",
  "license.bindMode.manual": "Manual",
  "license.bindMode.none": "No binding",
  "license.bindModeNoneWarning": "With binding off the server is capped at 8 players",
  "license.rotate": "Rotate the key",
  "license.rotateNote": "The old key stays valid for 24 more hours",
  "license.revoke": "Revoke",
  "license.resetIp": "Reset IP",
  "license.resetIpQuota": "Remaining today: {n}",
  "license.lastAuth": "Last authentication",
  "license.authCount": "Authentication count",
  "license.install": "Installation",

  // ── audit ──
  "audit.title": "Audit log",
  "audit.actor": "Actor",
  "audit.actionCol": "Action",
  "audit.target": "Target",
  "audit.before": "Before",
  "audit.after": "After",
  "audit.exportCsv": "Download CSV",
  "audit.emptyTitle": "No entries",

  // ── settings ──
  "settings.title": "Settings",
  "settings.general": "General",
  "settings.retention": "Retention",
  "settings.retentionNote": "Rows older than this are deleted by the hourly retention job",
  "settings.install": "Installation",
  "settings.dangerZone": "Danger zone",
  "settings.deleteServer": "Delete server",
  "settings.deleteConfirm": "Type the server name to delete it",
  "settings.deleteWarning": "Every license is revoked and the resource stops enforcing",
  "settings.account": "Account",
  "settings.profile": "Profile",
  "settings.changePassword": "Change password",
  "settings.language": "Language",
  "settings.apiKeys": "API keys",
  "settings.createKey": "Create key",
  "settings.scopes": "Scopes",
  "settings.serverScope": "Server scope",
  "settings.lastUsed": "Last used",
  "settings.keysEmpty": "No API keys",
  "settings.recentActivity": "Recent activity",
  "settings.keyCreated": "Key created",
  "settings.allServers": "All servers",
  "settings.expiresAt": "Expires at",
  "settings.profileSaved": "Profile updated",
  "settings.passwordChanged": "Your password changed; please sign in again",
  "settings.revokeKey": "Revoke key",
  "settings.revokeKeyConfirm": "Integrations using this key stop working immediately.",
  "settings.keyName": "Key name",
  "settings.keyIdentifier": "Key",
  "settings.keysEmptyDesc": "A personal key lets a bot or a script read your servers over the API.",
  "settings.noExpiry": "No expiry",
  "settings.expiry30d": "30 days",
  "settings.expiry90d": "90 days",
  "settings.expiry1y": "1 year",
  "settings.emailReadonly": "The email address cannot be changed",
  "settings.passwordNote": "Changing your password signs out every session, including this one.",
  "settings.scopesRequired": "Select at least one scope",
  "settings.scope.servers:read": "Read servers",
  "settings.scope.detections:read": "Read detections",
  "settings.scope.players:read": "Read players and the identifier lookup",
  "settings.scope.bans:read": "Read bans",
  "settings.scope.bans:write": "Issue and revoke bans",
  "settings.scope.commands:write": "Send commands",
  "settings.scope.config:read": "Read the configuration",

  // ── errors.<CODE> — one per ERROR_CODES member (SPEC §4.6) ──
  "errors.VALIDATION_ERROR": "The submitted values are invalid",
  "errors.AUTH_REQUIRED": "You must sign in to do this",
  "errors.AUTH_INVALID_CREDENTIALS": "Wrong email or password",
  "errors.AUTH_TOKEN_EXPIRED": "Your session has expired",
  "errors.AUTH_TOKEN_INVALID": "Invalid session, please sign in again",
  "errors.AUTH_REFRESH_REUSED": "Signed out for security reasons, please sign in again",
  "errors.AUTH_LOCKED": "Account temporarily locked, try again after {lockedUntil}",
  "errors.AUTH_REGISTRATION_CLOSED": "Registration is closed; an invitation link is required",
  "errors.FORBIDDEN": "You do not have permission to do this",
  "errors.NOT_FOUND": "Record not found",
  "errors.CONFLICT": "This record already exists",
  "errors.RATE_LIMITED": "Too many requests, please wait",
  "errors.LICENSE_INVALID": "The license key is invalid",
  "errors.LICENSE_REVOKED": "The license has been revoked",
  "errors.LICENSE_SUSPENDED": "The license is suspended",
  "errors.LICENSE_EXPIRED": "The license has expired",
  "errors.LICENSE_IP_MISMATCH": "The license is bound to a different IP",
  "errors.AGENT_VERSION_UNSUPPORTED": "The resource version is too old; update it",
  "errors.AGENT_SIGNATURE_INVALID": "The server signature could not be verified",
  "errors.HEARTBEAT_SEQ_REPLAY": "A replayed heartbeat was detected",
  "errors.BATCH_TOO_LARGE": "The submitted batch is too large",
  "errors.CONFIG_VERSION_CONFLICT": "The configuration changed in the meantime",
  "errors.CONFIG_INVALID": "The configuration is invalid",
  "errors.UPLOAD_TOKEN_INVALID": "The upload link is invalid or already used",
  "errors.UPLOAD_TOO_LARGE": "The file is too large (5 MB maximum)",
  "errors.UPLOAD_BAD_MIME": "Unsupported file type",
  "errors.COMMAND_EXPIRED": "The command has expired",
  "errors.SERVER_OFFLINE": "The server is offline; commands cannot be sent",
  "errors.GLOBAL_BAN_NOT_ELIGIBLE": "This ban is not eligible for the global list",
  "errors.WEBHOOK_INVALID_URL": "Enter a valid Discord webhook URL",
  "errors.INVITE_INVALID": "The invitation is invalid or has expired",
  "errors.INTERNAL": "An unexpected error occurred",

  // ── audit.action.<name> — the closed list of SPEC §3.4 ──
  "audit.action.auth.register": "Account registered",
  "audit.action.auth.login": "Signed in",
  "audit.action.auth.login_failed": "Failed sign-in attempt",
  "audit.action.auth.locked": "Account locked",
  "audit.action.auth.refresh_reuse": "Refresh token reused",
  "audit.action.auth.logout": "Signed out",
  "audit.action.auth.password_change": "Password changed",
  "audit.action.auth.password_reset": "Password reset",
  "audit.action.apikey.create": "API key created",
  "audit.action.apikey.revoke": "API key revoked",
  "audit.action.server.create": "Server created",
  "audit.action.server.update": "Server updated",
  "audit.action.server.delete": "Server deleted",
  "audit.action.server.transfer": "Server ownership transferred",
  "audit.action.member.invite": "Member invited",
  "audit.action.member.accept": "Invitation accepted",
  "audit.action.member.role": "Member role changed",
  "audit.action.member.remove": "Member removed",
  "audit.action.license.create": "License created",
  "audit.action.license.rotate": "License rotated",
  "audit.action.license.revoke": "License revoked",
  "audit.action.license.reset_ip": "License IP reset",
  "audit.action.license.bind_ip": "License IP bound",
  "audit.action.license.ip_mismatch": "License IP mismatch",
  "audit.action.license.update": "License updated",
  "audit.action.config.update": "Configuration updated",
  "audit.action.config.rollback": "Configuration rolled back",
  "audit.action.config.import": "Configuration imported",
  "audit.action.config.reset": "Configuration reset",
  "audit.action.ban.create": "Ban issued",
  "audit.action.ban.revoke": "Ban revoked",
  "audit.action.ban.update": "Ban updated",
  "audit.action.ban.import": "Ban list imported",
  "audit.action.ban.propose_global": "Global ban proposed",
  "audit.action.global_ban.review": "Global ban reviewed",
  "audit.action.global_ban.override": "Global ban override",
  "audit.action.whitelist.add": "Whitelist row added",
  "audit.action.whitelist.update": "Whitelist row updated",
  "audit.action.whitelist.remove": "Whitelist row removed",
  "audit.action.command.create": "Command sent",
  "audit.action.command.cancel": "Command cancelled",
  "audit.action.webhook.create": "Webhook created",
  "audit.action.webhook.update": "Webhook updated",
  "audit.action.webhook.delete": "Webhook deleted",
  "audit.action.webhook.test": "Webhook tested",
  "audit.action.webhook.disabled": "Webhook disabled",
  "audit.action.detection.review": "Detection reviewed",
  "audit.action.player.notes": "Player note updated",
  "audit.action.screenshot.delete": "Screenshot deleted",
  "audit.action.agent.auth": "Resource authenticated",
  "audit.action.agent.unlicensed": "Unlicensed resource request",

  // ── module.<detectionKey>.{name,desc,hint} — one triple per DETECTION_KEYS entry (65) ──
  // Authored once in docs/DETECTIONS.md §7 and transcribed here; i18n.test.ts fails on a missing triple.
  "module.godmode.name": "Godmode",
  "module.godmode.desc": "Detects a player who cannot be damaged.",
  "module.godmode.hint": "Admin or downed-state scripts that grant invincibility must call the SetGodmode export.",
  "module.health_over_max.name": "Health above maximum",
  "module.health_over_max.desc": "Detects health above the ped's own maximum.",
  "module.health_over_max.hint": "Servers running higher-health characters must raise the maxHealthCap option; health scripts should call the SetGodmode export.",
  "module.health_probe.name": "Health probe",
  "module.health_probe.desc": "Subtracts a little health to observe god modes that have no getter.",
  "module.health_probe.hint": "Downed and scripted-immortal states must call the SetGodmode export, otherwise the probe fights your script.",
  "module.armor_over_max.name": "Armour above maximum",
  "module.armor_over_max.desc": "Detects armour above the player's maximum.",
  "module.armor_over_max.hint": "Scripts that hand out armour must call the SetGodmode export.",
  "module.invisible.name": "Invisibility",
  "module.invisible.desc": "Detects a player making their own ped invisible.",
  "module.invisible.hint": "Cutscene or admin scripts that hide the ped must call the SetInvisible export.",
  "module.noclip.name": "Noclip",
  "module.noclip.desc": "Detects collision-free movement and passing through walls.",
  "module.noclip.hint": "Admin noclip tools must call the SetNoclip export.",
  "module.speed_hack.name": "Speed hack",
  "module.speed_hack.desc": "Detects on-foot movement faster than the engine allows.",
  "module.speed_hack.hint": "Scripts that grant extra speed must call the SetNoclip or AllowTeleport export.",
  "module.teleport.name": "Teleport",
  "module.teleport.desc": "Detects a position jump no vehicle or animation could produce.",
  "module.teleport.hint": "Garage, elevator and teleport scripts must call the AllowTeleport export.",
  "module.spectate.name": "Spectate",
  "module.spectate.desc": "Detects the spectator mode every cheat menu uses.",
  "module.spectate.hint": "Admin tools with a spectate feature must call the SetSpectate export.",
  "module.freecam.name": "Freecam",
  "module.freecam.desc": "Detects a rendered camera far away from the player ped.",
  "module.freecam.hint": "Cinematic and camera scripts must call the AllowScriptCam export.",
  "module.super_jump.name": "Super jump",
  "module.super_jump.desc": "Detects super jump enabled every frame.",
  "module.super_jump.hint": "Parkour and super-jump scripts must call the SetNoclip export.",
  "module.infinite_stamina.name": "Infinite stamina",
  "module.infinite_stamina.desc": "Detects stamina that does not drain while sprinting.",
  "module.infinite_stamina.hint": "Scripts that restore stamina must call the AllowStamina export.",
  "module.night_vision.name": "Night vision / thermal",
  "module.night_vision.desc": "Detects night vision or thermal enabled outside an authorised resource.",
  "module.night_vision.hint": "Add resources that offer night vision to whitelists.nightVisionResources, or call the AllowNightVision export.",
  "module.blacklisted_ped.name": "Blacklisted ped model",
  "module.blacklisted_ped.desc": "Detects use of a ped model on the blacklist.",
  "module.blacklisted_ped.hint": "Call the AllowModel export for legitimate model changes, or add the model to whitelists.models.",
  "module.blacklisted_weapon.name": "Blacklisted weapon",
  "module.blacklisted_weapon.desc": "Detects a weapon from the blacklist in the ped's inventory.",
  "module.blacklisted_weapon.hint": "Call the AllowWeapon export for weapons your jobs hand out.",
  "module.weapon_gained.name": "Unexplained weapon gain",
  "module.weapon_gained.desc": "Detects a weapon appearing that the server never handed out.",
  "module.weapon_gained.hint": "Scripts that give weapons must call the AllowWeapon export.",
  "module.infinite_ammo.name": "Infinite ammo",
  "module.infinite_ammo.desc": "Detects ammunition that does not decrease while firing.",
  "module.infinite_ammo.hint": "Shooting-range or mission scripts that grant unlimited ammo must call SetModuleBypass(src, 'infinite_ammo', seconds).",
  "module.explosive_ammo.name": "Explosive ammo",
  "module.explosive_ammo.desc": "Detects an explosive or incendiary damage type on a bullet weapon.",
  "module.explosive_ammo.hint": "Scripts that grant explosive rounds must call the AllowExplosion export.",
  "module.damage_modifier.name": "Damage modifier",
  "module.damage_modifier.desc": "Detects damage or defence multipliers set on the local player.",
  "module.damage_modifier.hint": "Scripts that use damage multipliers must call SetModuleBypass(src, 'damage_modifier', seconds).",
  "module.weapon_damage_meta.name": "Weapon damage meta change",
  "module.weapon_damage_meta.desc": "Detects weapon damage values drifting from the session baseline.",
  "module.weapon_damage_meta.hint": "Scripts that patch damage at runtime must call SetModuleBypass(src, 'weapon_damage_meta', seconds).",
  "module.silent_aim.name": "Silent aim",
  "module.silent_aim.desc": "Detects bullets landing far off the axis the camera was pointing down.",
  "module.silent_aim.hint": "A behavioural heuristic, not proof: an external aimbot cannot be observed from Lua. Leave ignoreInVehicle on if your scripts shoot from vehicles.",
  "module.shot_cadence.name": "Rate of fire",
  "module.shot_cadence.desc": "Detects shots fired faster than the weapon can mechanically cycle.",
  "module.shot_cadence.hint": "If your weapons.meta raises a weapon's fire rate, add that weapon to ignoredWeapons.",
  "module.no_recoil.name": "No recoil",
  "module.no_recoil.desc": "Detects shots that never kick the camera, which means recoil has been removed.",
  "module.no_recoil.hint": "If your weapons.meta genuinely removes a weapon's recoil, add that weapon to ignoredWeapons.",
  "module.magic_bullet.name": "Magic bullet",
  "module.magic_bullet.desc": "Detects a single shot damaging more players than one bullet can reach.",
  "module.magic_bullet.hint": "The default allows 2 victims for a round that genuinely penetrates; explosions never enter this module.",
  "module.server_headshot_ratio.name": "Headshot ratio",
  "module.server_headshot_ratio.desc": "Measures, server-side, what share of a player's hits land on the head.",
  "module.server_headshot_ratio.hint": "Logs only by default. Do not escalate to a punishment before looking at your own server's median.",
  "module.weapon_damage_event.name": "Forged weapon damage event",
  "module.weapon_damage_event.desc": "Detects a weapon damage event the sending client could not produce.",
  "module.weapon_damage_event.hint": "Scripts that produce custom weapon damage must call the AllowWeapon export.",
  "module.give_weapon_event.name": "Give weapon event",
  "module.give_weapon_event.desc": "Detects giveWeaponEvent for a blacklisted weapon or a ped the sender does not own.",
  "module.give_weapon_event.hint": "Scripts that distribute weapons must call the AllowWeapon export.",
  "module.remove_weapon_event.name": "Remove weapon event",
  "module.remove_weapon_event.desc": "Detects removeWeaponEvent aimed at a ped the sender does not own.",
  "module.remove_weapon_event.hint": "Scripts that disarm players must call AllowWeapon or SetModuleBypass(src, 'remove_weapon_event', seconds).",
  "module.vehicle_modifier.name": "Vehicle power modifier",
  "module.vehicle_modifier.desc": "Detects speed, power or gravity multipliers applied to the driven vehicle.",
  "module.vehicle_modifier.hint": "Tuning and racing scripts must call the AllowVehicleMod export.",
  "module.vehicle_speed.name": "Vehicle overspeed",
  "module.vehicle_speed.desc": "Detects a vehicle far above its own estimated top speed.",
  "module.vehicle_speed.hint": "Nitro and racing scripts must call the AllowVehicleMod export.",
  "module.vehicle_handling.name": "Vehicle handling change",
  "module.vehicle_handling.desc": "Detects runtime handling fields far above the observed baseline.",
  "module.vehicle_handling.hint": "Tuning scripts that edit handling must call the AllowVehicleMod export, which also re-baselines the values.",
  "module.vehicle_godmode.name": "Vehicle godmode",
  "module.vehicle_godmode.desc": "Detects engine and body health pinned at maximum despite impacts.",
  "module.vehicle_godmode.hint": "Repair and garage scripts must call the AllowVehicleMod export.",
  "module.vehicle_invisible.name": "Invisible vehicle",
  "module.vehicle_invisible.desc": "Detects the driven vehicle being made invisible.",
  "module.vehicle_invisible.hint": "Both alpha and script visibility must agree, so a vehicle fading in at spawn is not flagged.",
  "module.vehicle_noclip.name": "Vehicle noclip",
  "module.vehicle_noclip.desc": "Detects collision being switched off on the driven vehicle.",
  "module.vehicle_noclip.hint": "A memory noclip never touches this flag; the server-side position check is what catches that form.",
  "module.vehicle_gravity.name": "Vehicle gravity",
  "module.vehicle_gravity.desc": "Detects vehicle gravity moved away from the configured default.",
  "module.vehicle_gravity.hint": "Running a low-gravity event? Set defaultGravity to your own number rather than turning the module off.",
  "module.player_blips.name": "Player blips (ESP)",
  "module.player_blips.desc": "Detects blips on remote players that no registered resource created.",
  "module.player_blips.hint": "Scripts that create blips must call the client-side RegisterBlip export, or add the sprite to whitelists.blipSprites.",
  "module.entity_spam_client.name": "Client-side entity spam",
  "module.entity_spam_client.desc": "Detects a burst of locally owned script entities around the player.",
  "module.entity_spam_client.hint": "Scripts that spawn many entities at once must call the AllowEntitySpawn export.",
  "module.menu_texture.name": "Cheat menu texture",
  "module.menu_texture.desc": "Detects texture dictionaries only a cheat menu registers.",
  "module.menu_texture.hint": "Add your own UI texture dictionaries to whitelists.textures.",
  "module.menu_key.name": "Cheat menu key",
  "module.menu_key.desc": "Watches for presses of the keys cheat menus bind, for the record only.",
  "module.menu_key.hint": "This module never punishes; use the IgnorePlayer export if it still bothers a staff client.",
  "module.resource_injection.name": "Resource injection",
  "module.resource_injection.desc": "Detects a resource on the client the server never started.",
  "module.resource_injection.hint": "Add your legitimate client-side resources to whitelists.resources.",
  "module.resource_stopped.name": "Anticheat stopped",
  "module.resource_stopped.desc": "Detects the SentinelAC client script being stopped or blocked.",
  "module.resource_stopped.hint": "Use the IgnorePlayer export for test clients; there is no other bypass.",
  "module.client_integrity.name": "Client file integrity",
  "module.client_integrity.desc": "Detects SentinelAC client files that do not match the server's hashes.",
  "module.client_integrity.hint": "It only ever logs; use the IgnorePlayer export for a known test client.",
  "module.client_honeypot.name": "Client honeypot",
  "module.client_honeypot.desc": "Detects a client-side decoy event, export or command being triggered.",
  "module.client_honeypot.hint": "There is no bypass by design; only the staff ACE and the IgnorePlayer export exempt a player.",
  "module.heartbeat_missing.name": "Heartbeat missing",
  "module.heartbeat_missing.desc": "Detects a connected client that stops answering the anticheat challenge.",
  "module.heartbeat_missing.hint": "For heavily loaded test clients call SetModuleBypass(src, 'heartbeat_missing', seconds).",
  "module.honeypot_event.name": "Server honeypot event",
  "module.honeypot_event.desc": "Detects a client triggering a server-only decoy event.",
  "module.honeypot_event.hint": "There is no bypass by design; these names are never sent to any client.",
  "module.honeypot_export.name": "Server honeypot export",
  "module.honeypot_export.desc": "Detects a resource calling a decoy export that promises to disable the anticheat.",
  "module.honeypot_export.hint": "There is no bypass by design; audit the calling resource when this fires.",
  "module.honeypot_command.name": "Server honeypot command",
  "module.honeypot_command.desc": "Detects a player running a decoy command that promises to disable the anticheat.",
  "module.honeypot_command.hint": "There is no bypass by design; only the staff ACE exempts a player.",
  "module.explosion_spam.name": "Explosion spam",
  "module.explosion_spam.desc": "Detects more explosions per window than any legitimate weapon can produce.",
  "module.explosion_spam.hint": "Mission and effect scripts that create explosions must call the AllowExplosion export.",
  "module.explosion_blacklist.name": "Blacklisted explosion",
  "module.explosion_blacklist.desc": "Detects creation of an explosion type on the blacklist.",
  "module.explosion_blacklist.hint": "Scripts that create legitimate explosions must call the AllowExplosion export.",
  "module.fire_spam.name": "Fire spam",
  "module.fire_spam.desc": "Detects mass fire creation.",
  "module.fire_spam.hint": "Firefighter and fire-scenario scripts must call the AllowExplosion export.",
  "module.clear_ped_tasks.name": "Clear ped tasks abuse",
  "module.clear_ped_tasks.desc": "Detects clearPedTasksEvent aimed at another player's ped.",
  "module.clear_ped_tasks.hint": "Scripts that clear another player's tasks must call SetModuleBypass(src, 'clear_ped_tasks', seconds).",
  "module.projectile_spam.name": "Projectile spam",
  "module.projectile_spam.desc": "Detects an impossible rate of projectile launches.",
  "module.projectile_spam.hint": "Firework and explosive scripts must call the AllowExplosion export.",
  "module.ptfx_blacklist.name": "Blacklisted particle effect",
  "module.ptfx_blacklist.desc": "Detects particle effects from blacklisted asset dictionaries.",
  "module.ptfx_blacklist.hint": "Scripts that use these effects must call SetModuleBypass(src, 'ptfx_blacklist', seconds).",
  "module.entity_spawn_rate.name": "Entity spawn rate",
  "module.entity_spawn_rate.desc": "Detects a player creating more networked entities than any legitimate flow needs.",
  "module.entity_spawn_rate.hint": "Scripts that spawn in bulk must call the AllowEntitySpawn export.",
  "module.entity_blacklisted_model.name": "Blacklisted entity model",
  "module.entity_blacklisted_model.desc": "Detects creation of a vehicle, ped or object model on the blacklist.",
  "module.entity_blacklisted_model.hint": "Call the AllowModel export for legitimate use, or add the model to whitelists.models.",
  "module.entity_unauthorized_script.name": "Entity from unauthorised script",
  "module.entity_unauthorized_script.desc": "Detects a networked entity whose creating script is not whitelisted.",
  "module.entity_unauthorized_script.hint": "Add your own spawner resources to whitelists.entityScripts.",
  "module.server_godmode.name": "Server-verified godmode",
  "module.server_godmode.desc": "Detects invincibility measured by the server itself.",
  "module.server_godmode.hint": "Downed and scripted-immortal states must call the SetGodmode export.",
  "module.server_super_jump.name": "Server-verified super jump",
  "module.server_super_jump.desc": "Detects super jump read authoritatively on the server.",
  "module.server_super_jump.hint": "Parkour scripts must call the SetNoclip export.",
  "module.server_freecam.name": "Server-verified freecam",
  "module.server_freecam.desc": "Detects a camera far from the ped, measured on the server.",
  "module.server_freecam.hint": "Cinematic scripts must call the AllowScriptCam export.",
  "module.server_teleport.name": "Server-verified teleport",
  "module.server_teleport.desc": "Detects an impossible position delta measured from the server's own copy.",
  "module.server_teleport.hint": "Garage, elevator and teleport scripts must call the AllowTeleport export.",
  "module.server_speed.name": "Server-verified speed hack",
  "module.server_speed.desc": "Detects sustained on-foot speed above the engine ceiling, measured on the server.",
  "module.server_speed.hint": "Scripts that grant extra speed must call the SetNoclip or AllowTeleport export.",
  "module.chat_spam.name": "Chat spam",
  "module.chat_spam.desc": "Detects chat flooding and over-long messages.",
  "module.chat_spam.hint": "Automated announcement bots must call SetModuleBypass(src, 'chat_spam', seconds).",
  "module.chat_blacklisted_string.name": "Blacklisted chat string",
  "module.chat_blacklisted_string.desc": "Detects chat messages advertising a known cheat menu.",
  "module.chat_blacklisted_string.hint": "Remove a term that matches legitimate chat from blacklists.chatStrings.",
  "module.event_spam.name": "Event spam",
  "module.event_spam.desc": "Detects a client firing server net events far above any legitimate rate.",
  "module.event_spam.hint": "Raise the limit for a busy event with RegisterProtectedEvent(name, { max, windowMs }).",
  "module.blacklisted_event.name": "Blacklisted event",
  "module.blacklisted_event.desc": "Detects a client triggering an event only a cheat menu uses.",
  "module.blacklisted_event.hint": "Remove a name that collides with your own resources from blacklists.events.",
  "module.safe_event_invalid.name": "Invalid safe-event token",
  "module.safe_event_invalid.desc": "Detects a registered Safe Event arriving without a valid token.",
  "module.safe_event_invalid.hint": "Trigger safe events only through the SentinelAC client export.",
  "module.event_rule.name": "Event rule",
  "module.event_rule.desc": "Reports violations of the event rules you write yourself.",
  "module.event_rule.hint": "The rules live in Config > Event rules. Each rule carries its own action; this module is only the on/off switch and the strike window.",
  "module.resource_list_mismatch.name": "Resource list mismatch",
  "module.resource_list_mismatch.desc": "Detects a client resource list that does not match the server's.",
  "module.resource_list_mismatch.hint": "Add resources that load conditionally on the client to whitelists.resources.",
  "module.connect_offline.name": "Offline connect check",
  "module.connect_offline.desc": "Records joins admitted from the local ban snapshot while the cloud was unreachable.",
  "module.connect_offline.hint": "This is not an accusation: nothing is enforced, the row exists only for the audit trail.",
  "module.custom.name": "Custom detection",
  "module.custom.desc": "Carries whatever your own resources report through the Report export.",
  "module.custom.hint": "Its meaning is defined by the caller; use SetModuleBypass(src, 'custom', seconds) for a noisy one.",
  "module.connect_ban_evasion.name": "Ban evasion attempt",
  "module.connect_ban_evasion.desc": "Detects a joining player whose hardware tokens match an active ban under new identifiers.",
  "module.connect_ban_evasion.hint": "For players who genuinely share a machine, add a whitelist row with bypassConnect set.",
  "module.connect_missing_identifier.name": "Missing identifier",
  "module.connect_missing_identifier.desc": "Detects a join that does not present every required identifier type.",
  "module.connect_missing_identifier.hint": "Add a whitelist row for the player to grant an exception.",
  "module.connect_vpn.name": "VPN / proxy connection",
  "module.connect_vpn.desc": "Detects a join from a VPN, proxy or hosting-provider address.",
  "module.connect_vpn.hint": "For players with a legitimate VPN, add a whitelist row with bypassConnect set.",
  "module.connect_name_filter.name": "Blocked player name",
  "module.connect_name_filter.desc": "Detects player names matching a blocked pattern or exceeding the length limit.",
  "module.connect_name_filter.hint": "Remove an over-broad pattern from connect.nameBlacklistPatterns.",
  "module.connect_low_trust.name": "Low trust score",
  "module.connect_low_trust.desc": "Catches players joining with a trust score below the configured threshold.",
  "module.connect_low_trust.hint": "The `Log` default is shadow mode: arm the threshold, watch who it would refuse in the live feed, then promote it to `Kick`.",

  // ── staff pages: bans, global bans, whitelist, screenshots, commands ──
  "common.from": "From",
  "common.to": "To",
  "common.statusLabel": "Status",
  "ban.newDesc": "Pick a target or paste identifiers; what was understood is shown before the ban is created.",
  "ban.targetPlayer": "Pick a player",
  "ban.targetIdentifiers": "Paste identifiers",
  "ban.targetRequired": "Pick a player",
  "ban.targetPlayerUnnamed": "unnamed player",
  "ban.needHardIdentifier": "At least one hard identifier is required: license, license2, steam, discord or fivem",
  "ban.parsedTitle": "Identifiers understood",
  "ban.parsedEmpty": "No valid identifier yet",
  "ban.parsedInvalid": "{n} line(s) not understood: {lines}",
  "ban.playerNameLabel": "Player name",
  "ban.reasonRequired": "A reason is required",
  "ban.proposeGlobalDesc": "Once approved, this ban applies on every server that enforces the global list.",
  "ban.proposeGlobalNeedsEvidence": "A proposal needs a screenshot or a deterministic module",
  "ban.confirmTitle": "Confirm the ban",
  "ban.confirmKick": "The player will be kicked immediately if they are online.",
  "ban.confirmGlobal": "This ban will be proposed to the global list.",
  "ban.created": "Ban created ({code})",
  "ban.durationDaysValue": "{n} day(s)",
  "ban.emptyDesc": "This server has no bans yet.",
  "ban.searchPlaceholder": "Player name or ban code",
  "ban.sourceLabel": "Source",
  "ban.source.system": "System",
  "ban.source.panel": "Panel",
  "ban.source.export": "Export",
  "ban.source.command": "Command",
  "ban.source.import": "Import",
  "ban.source.evasion": "Ban evasion",
  "ban.importDesc": "Paste a txAdmin or SentinelAC export here.",
  "ban.importFormat": "Format",
  "ban.importFormat.txadmin": "txAdmin",
  "ban.importFormat.sentinelac": "SentinelAC",
  "ban.importFormat.json": "JSON",
  "ban.importData": "Data",
  "ban.importDataHint": "Up to 20,000 entries, 1 MiB.",
  "ban.importResultTitle": "Import result",
  "ban.identifiersTitle": "Identifiers",
  "ban.identifierHard": "Hard",
  "ban.identifierSoft": "Soft",
  "ban.identifierInactive": "Inactive",
  "ban.notActive": "This ban is no longer active",
  "ban.alreadyProposed": "This ban has already been proposed",
  "ban.revokeConfirm": "Ban {code} will be revoked — player: {player}. They will be able to reconnect.",
  "ban.revoked": "Ban revoked ({code})",
  "ban.changeExpiryConfirm": "The expiry of ban {code} will change.",
  "ban.changeExpiryHint": "Leave it empty to make the ban permanent.",
  "ban.reporterAnonymised": "The reporting server is always anonymised.",
  "ban.maskedHint": "This identifier arrived masked",
  "ban.maskedNote": "Global-list identifiers arrive masked. Only a platform admin and the proposing server's owner ever see the full values.",
  "ban.eligibility.contribution_disabled": "Contribution must be on",
  "ban.eligibility.server_too_new": "The server must be at least 7 days old",
  "ban.eligibility.reputation_below_40": "Reputation must be at least 40",
  "ban.eligibility.no_evidence": "A screenshot or a deterministic module is required",
  "ban.enforceHint": "The enforcement mode is written into config history.",
  "ban.contributeHint": "Lets you propose your own bans to the global list.",
  "ban.mode.block": "Block",
  "ban.mode.flag": "Flag only",
  "ban.mode.off": "Off",
  "ban.whyBlockedHint": "Only hard identifiers match globally; ip and token never do.",
  "ban.whyBlockedNoMatch": "This identifier is not on the global list.",
  "ban.globalEmptyTitle": "No global bans",
  "ban.globalEmptyDesc": "There is no approved global ban right now.",
  "ban.exemptConfirm": "Global ban {code} will no longer apply on this server. Reason: {reason}",
  "ban.exemptDone": "Exemption added ({code})",
  "ban.queueEmptyDesc": "No proposal is waiting for review.",
  "ban.reviewNote": "Review note",
  "ban.reviewConfirm": "Proposal {code} — reason: {reason}, reported by: {proposer}",
  "ban.reviewReputationNote": "An approval grants the proposing server +2 reputation, a rejection costs 10.",
  "ban.reputationPlus": "+2 reputation to the proposing server",
  "ban.reputationMinus": "−10 reputation to the proposing server",
  "whitelist.emptyDesc": "No player is exempt.",
  "whitelist.editTitle": "Edit whitelist row",
  "whitelist.dialogDesc": "Pick an identifier and a scope. With the all-modules scope the player is exempt from every check.",
  "whitelist.valueRequired": "An identifier value is required",
  "whitelist.modulesRequired": "Pick at least one module",
  "whitelist.labelHint": "Write down why this row exists.",
  "whitelist.bypassConnectDesc": "Only has an effect with the all-modules scope; it then skips the connect ban check, global bans included.",
  "whitelist.fullBypassTitle": "Full bypass",
  "whitelist.fullBypassDesc": "This row silences every module and skips the ban check on connect. Use it only for identifiers you genuinely trust.",
  "whitelist.expiresHint": "Leave it empty to make the row permanent.",
  "whitelist.scopeAllWarning": "All modules selected: this identifier trips no check at all.",
  "whitelist.scopeModulesHint": "{n} module(s) selected. Every other module keeps running.",
  "whitelist.scopeModulesCount": "{n} module(s)",
  "whitelist.scopeRuleTitle": "Scope rule",
  "whitelist.scopeRuleDesc": "The all-modules scope silences every check; a selected-modules scope silences only those modules and never skips the connect check.",
  "whitelist.conflictTitle": "This identifier is already listed",
  "whitelist.conflictDesc": "One identifier can only have one row; edit the existing one.",
  "whitelist.conflictRow": "Existing row: {value}",
  "whitelist.added": "Added to the whitelist ({value})",
  "whitelist.removeTitle": "Delete whitelist row",
  "whitelist.removeConfirm": "Row {value} will be deleted. Current scope: {scope}.",
  "whitelist.removed": "Row deleted ({value})",
  "whitelist.fromPlayerDesc": "One row is created per hard identifier the player is known by.",
  "whitelist.fromPlayerResult": "{created} row(s) created, {skipped} skipped",
  "whitelist.skippedOther": "Could not be added",
  "screenshot.status.pending": "Pending",
  "screenshot.status.uploaded": "Uploaded",
  "screenshot.status.failed": "Failed",
  "screenshot.status.expired": "Expired",
  "screenshot.dimensions": "Dimensions",
  "screenshot.mime": "Type",
  "screenshot.signedUrlCopied": "Shareable link copied",
  "screenshot.requestDesc": "Requests a fresh frame from an online player.",
  "screenshot.requestTarget": "Target: {player} · #{source}",
  "screenshot.cooldown": "Cooldown: {seconds} s",
  "screenshot.evidenceOnly": "A screenshot is evidence only: a missing frame proves nothing.",
  "screenshot.deleteTitle": "Delete screenshot",
  "screenshot.deleteConfirm": "The screenshot of {player} from {at} will be deleted permanently.",
  "screenshot.deleted": "Screenshot deleted",
  "command.type.ban": "Ban",
  "command.type.unban": "Unban",
  "command.playerOffline": "Offline",
  "command.payload": "Payload",
  "command.attempts": "Attempts",
  "command.result": "Result",
  "command.audience": "Audience",
  "command.banHint": "Bans and unbans are never sent from here; the bans page is the only path:",
  "command.incomplete": "Fill in the target and the text first",
  "command.broadcastWarning": "The message goes to everyone on the server.",
  "command.cancelTitle": "Cancel command",
  "command.cancelConfirm": "The queued {type} command will be cancelled: {target}",
  "command.cancelled": "Command cancelled",
  "command.cancelTooLate": "The command was already delivered and could not be cancelled",
  "command.emptyDesc": "No command has been sent to this server yet.",

  // ── owner administration: config editor, discord, team, licenses, audit, settings ──
  "common.selectAll": "Select all",
  "common.selectRow": "Select row",
  "common.removeItem": "Remove {item}",
  "common.tagPlaceholder": "Type and press Enter",
  "common.itemCount": "{n} items",
  "common.itemCountMax": "{n}/{max}",
  "common.maxItems": "At most {n} items",
  "common.numberOnly": "Numbers only",
  "common.unitSecond": "s",
  "common.unitHour": "h",
  "common.unitDay": "days",
  "common.durationDays": "{n} days",
  "common.durationHours": "{n} hours",
  "config.field": "Field",
  "config.noChanges": "No changes",
  "config.diffOnlyChanged": "Only changed fields are shown",
  "config.diffCurrent": "Saved",
  "config.diffDraft": "Draft",
  "config.currentVersion": "current",
  "config.systemAuthor": "System",
  "config.searchPlaceholder": "Search modules",
  "config.groupCount": "{enabled}/{total} enabled",
  "config.logOnlyDesc": "While this is on no module kicks or bans; every module only logs.",
  "config.logOnlySuffix": "→ log",
  "config.deterministic": "Deterministic",
  "config.deterministicHint": "No legitimate path produces this signal, so it may back a global-ban proposal.",
  "config.serverVerifiable": "Server-verifiable",
  "config.serverVerifiableHint": "When the server cannot confirm the claim, a ban is downgraded to a kick.",
  "config.noOptions": "This module has no options",
  "config.optionsHint": "Field names are the module schema’s own identifiers and are not translated.",
  "config.rangeHint": "{min} – {max}",
  "config.minHint": "min {min}",
  "config.maxHint": "max {max}",
  "config.preview": "Preview",
  "config.insertPlaceholder": "Insert a placeholder:",
  "config.honeypotNote": "Server honeypot names are never sent to clients; the client list ships in the game bundle.",
  "config.vpnPrivacyNotice": "This setting sends the joining player’s IP address to a third-party service (KVKK/GDPR).",
  "config.historyEmpty": "No version history yet",
  "config.alreadyCurrent": "This version is already the current one",
  "config.rollbackWarning": "A NEW version with the content of v{version} is inserted; nothing is deleted from the history.",
  "config.resetWarning": "{n} fields will return to their defaults.",
  "config.changedElsewhereDesc": "Your draft was kept. You can re-apply your changes on top of the new version.",
  "config.conflictTheirs": "The other admin’s change",
  "config.conflictMine": "Your change",
  "config.conflictLeaves": "{n} fields were changed by both sides",
  "config.conflictKeepMine": "Mine: {mine} · theirs: {theirs}",
  "config.importDrop": "Drop a JSON file here",
  "config.importChooseFile": "Choose a file",
  "config.importPaste": "or paste JSON",
  "config.importNotJson": "Not valid JSON",
  "config.importNothing": "Load a valid configuration first",
  "config.sectionGeneral": "General",
  "config.sectionMessages": "Player-facing messages",
  "config.sectionTiming": "Timing",
  "config.sectionTimingDesc": "Controls when modules start reporting after a player spawns.",
  "config.sectionReportLimit": "Report rate limit",
  "config.sectionReportLimitDesc": "The most reports one client may send per window.",
  "config.sectionOffline": "When the cloud is unreachable",
  "config.sectionOfflineDesc": "What to do when the cloud does not answer while a player is connecting.",
  "config.sectionIdentity": "Identity and name",
  "config.sectionEnforcement": "Enforcement",
  "config.sectionConnectMessages": "Connection messages",
  "config.sectionBypass": "Staff bypass",
  "config.sectionBypassDesc": "Staff holding these ACE permissions are exempt from every module.",
  "config.sectionSafeEvents": "Safe events",
  "config.sectionSafeEventsDesc": "Resource events are signed with a single-use token.",
  "config.sectionScreenshots": "Screenshots",
  "config.sectionBlacklists": "Blacklists",
  "config.sectionBlacklistsDesc": "Entries in these lists are forbidden.",
  "config.sectionWhitelists": "Whitelists",
  "config.sectionWhitelistsDesc": "Entries in these lists are exempted from the matching checks.",
  "config.sectionHoneypots": "Honeypots",
  "config.offlinePolicy.cache": "Use the cache",
  "config.offlinePolicy.cacheDesc": "The last known ban list decides; a stale list can let a new ban through.",
  "config.offlinePolicy.allow": "Let everyone in",
  "config.offlinePolicy.allowDesc": "While the cloud is unreachable banned players can join too.",
  "config.offlinePolicy.deny": "Let nobody in",
  "config.offlinePolicy.denyDesc": "While the cloud is unreachable nobody can join at all.",
  "config.field.general.framework": "Framework",
  "config.field.general.debug": "Debug",
  "config.field.general.debugDesc": "The resource writes verbose logs to the server console.",
  "config.field.general.txAdminAdminsBypass": "txAdmin admins are exempt",
  "config.field.general.txAdminAdminsBypassDesc": "Anyone with txAdmin admin rights is exempt from every module.",
  "config.field.general.mirrorTxAdminBans": "Mirror txAdmin bans",
  "config.field.general.mirrorTxAdminBansDesc": "Bans issued through txAdmin are recorded in SentinelAC as well.",
  "config.field.general.banMessage": "Ban message",
  "config.field.general.kickMessage": "Kick message",
  "config.field.general.appealUrl": "Appeal URL",
  "config.field.general.appealUrlHint": "Used by the {appealUrl} placeholder.",
  "config.field.general.spawnGraceMs": "Grace after spawn",
  "config.field.general.frameworkGraceMs": "Framework grace",
  "config.field.general.readySettleMs": "Ready settle",
  "config.field.general.clientHeartbeatIntervalMs": "Client heartbeat interval",
  "config.field.general.clientHeartbeatMissLimit": "Missed heartbeats allowed",
  "config.field.general.reportRateLimit.max": "Reports per window",
  "config.field.general.reportRateLimit.windowMs": "Window",
  "config.field.connect.offlinePolicy": "When the cloud is unreachable",
  "config.field.connect.timeoutMs": "Cloud timeout",
  "config.field.connect.requiredIdentifiers": "Required identifiers",
  "config.field.connect.nameBlacklistPatterns": "Blocked name patterns",
  "config.field.connect.nameBlacklistPatternsHint": "Case-insensitive substring match.",
  "config.field.connect.globalBans": "Global bans",
  "config.field.connect.banByIp": "Ban by IP as well",
  "config.field.connect.banByIpDesc": "On a shared IP this can block innocent players too.",
  "config.field.connect.minTrustScore": "Minimum trust score",
  "config.field.connect.maxSessionsPerIp": "Sessions per IP",
  "config.field.connect.vpnCheck": "VPN/proxy check",
  "config.field.connect.vpnCheckDesc": "The joining IP is queried against a third-party service.",
  "config.field.connect.deferralMessage": "Deferral message",
  "config.field.connect.messages.banned": "Banned player",
  "config.field.connect.messages.globalBanned": "Globally banned player",
  "config.field.connect.messages.unreachable": "Cloud unreachable",
  "config.field.connect.messages.missingIdentifiers": "Missing identifiers",
  "config.field.connect.messages.lowTrust": "Low trust score",
  "config.field.bypass.staffAce": "Staff ACE permission",
  "config.field.bypass.adminAce": "Admin ACE permission",
  "config.field.bypass.staffIdentifiers": "Exempt identifiers",
  "config.field.bypass.staffIdentifiersHint": "license:… · steam:… · discord:…",
  "config.field.safeEvents.enabled": "Enabled",
  "config.field.safeEvents.strict": "Strict mode",
  "config.field.safeEvents.strictDesc": "Escalates an invalid token from log to ban.",
  "config.field.safeEvents.tokenTtlMs": "Token lifetime",
  "config.field.screenshots.onBan": "Capture a screenshot on ban",
  "config.field.screenshots.onSeverity": "Capture from this severity up",
  "config.field.screenshots.encoding": "Format",
  "config.field.screenshots.quality": "Quality",
  "config.field.screenshots.perPlayerCooldownMs": "Per-player cooldown",
  "config.field.screenshots.perServerPerMinute": "Max per minute",
  "config.field.blacklists.weapons": "Blocked weapons",
  "config.field.blacklists.peds": "Blocked peds",
  "config.field.blacklists.vehicles": "Blocked vehicles",
  "config.field.blacklists.objects": "Blocked objects",
  "config.field.blacklists.explosionTypes": "Blocked explosion types",
  "config.field.blacklists.ptfx": "Blocked particle effects",
  "config.field.blacklists.events": "Blocked events",
  "config.field.blacklists.eventStrings": "Blocked event strings",
  "config.field.blacklists.chatStrings": "Blocked chat strings",
  "config.field.whitelists.resources": "Allowed resources",
  "config.field.whitelists.entityScripts": "Allowed entity scripts",
  "config.field.whitelists.models": "Allowed models",
  "config.field.whitelists.blipSprites": "Allowed blip sprites",
  "config.field.whitelists.nightVisionResources": "Resources allowed to use night vision",
  "config.field.whitelists.textures": "Allowed textures",
  "config.field.honeypots.clientEvents": "Client honeypot events",
  "config.field.honeypots.clientExports": "Client honeypot exports",
  "config.field.honeypots.clientCommands": "Client honeypot commands",
  "config.field.honeypots.serverEvents": "Server honeypot events",
  "config.field.honeypots.serverExports": "Server honeypot exports",
  "config.field.honeypots.serverCommands": "Server honeypot commands",
  "discord.name": "Name",
  "discord.enabled": "Enabled",
  "discord.created": "Webhook added",
  "discord.updated": "Webhook updated",
  "discord.deleted": "Webhook deleted",
  "discord.deleteTitle": "Delete webhook",
  "discord.deleteWarning": "{name} will be deleted. This cannot be undone.",
  "discord.emptyDesc": "Send detection, ban and server-status alerts to your Discord channel.",
  "discord.securityTitle": "Webhook URL security",
  "discord.eventsRequired": "Select at least one event",
  "discord.failureCount": "{n} failed deliveries",
  "discord.lastSuccess": "Last successful delivery",
  "discord.testFailed": "Test failed (HTTP {status})",
  "discord.mentionRoleHint": "Discord role id (digits only)",
  "discord.urlHintNew": "Copy the webhook URL from your Discord channel settings.",
  "discord.urlHintEdit": "Leave it empty to keep the stored URL: {hint}",
  "discord.screenshotSlot": "A screenshot is attached",
  "discord.event.detection": "Detection",
  "discord.event.ban": "Ban",
  "discord.event.unban": "Unban",
  "discord.event.kick": "Kick",
  "discord.event.player_join": "Player join",
  "discord.event.server_status": "Server status",
  "discord.event.command_result": "Command result",
  "discord.event.screenshot_ready": "Screenshot ready",
  "discord.event.global_ban_match": "Global ban match",
  "team.member": "Member",
  "team.permission": "Permission",
  "team.status": "Status",
  "team.status.active": "Active",
  "team.status.invited": "Invited",
  "team.emptyMembers": "No members yet",
  "team.expires": "expires",
  "team.resend": "Resend",
  "team.revokeInvite": "Revoke invite",
  "team.inviteOnce": "This link will not be shown again",
  "team.inviteOnceDesc": "Send this link to {email}. Resending it invalidates the old link.",
  "team.cannotChangeSelf": "You cannot change your own role",
  "team.cannotManagePeer": "You cannot manage a role at or above your own",
  "team.cannotRemoveOwner": "The owner cannot be removed; ownership can only be transferred",
  "team.cannotRemoveSelf": "You cannot remove yourself",
  "team.alreadyOwner": "This member is already the owner",
  "team.transferNeedsActive": "Ownership can only be transferred to an active member",
  "team.removeWarning": "{name} will be removed from this server.",
  "team.transferWarning": "{name} becomes the owner and you become an admin. Only the new owner can undo this.",
  "team.transferred": "Ownership transferred to {name}",
  "license.emptyTitle": "No active license",
  "license.emptyDesc": "The resource needs a key before it can authenticate.",
  "license.issueNew": "Issue a new key",
  "license.noActive": "There is no active license",
  "license.status.active": "Active",
  "license.status.suspended": "Suspended",
  "license.status.revoked": "Revoked",
  "license.boundAt": "Bound at",
  "license.lastAuthIp": "Last auth IP",
  "license.expiresAt": "Expires",
  "license.ipResetCount": "IP resets",
  "license.bindMode.first_useDesc": "Binds to the IP on the first successful auth.",
  "license.bindMode.manualDesc": "You enter the IP yourself.",
  "license.bindMode.noneDesc": "No IP binding at all; development only.",
  "license.bindModeSaved": "Bind mode saved",
  "license.manualIpHint": "The game server’s public IP address",
  "license.resetIpWarning": "The bound IP is cleared and the resource rebinds on its next auth. Remaining today: {n}",
  "license.resetIpDone": "IP reset",
  "license.rotateWarning": "A new key is created and shown once. The old key keeps working for 24 hours.",
  "license.rotateRevealDesc": "Copy the key now; it cannot be shown again once you close this. If you lose it, rotating again is the only recovery.",
  "license.graceUntil": "The old key stays valid until {time}",
  "license.revokeWarning": "This key is revoked permanently; the resource stops enforcing at its next auth.",
  "license.revoked": "License revoked",
  "license.revokeLastActive": "This is the last active license; rotate it first.",
  "license.supersededTitle": "The superseded key is still valid ({keyId})",
  "license.supersededDesc": "Both keys authenticate until the rotation grace window ends:",
  "audit.time": "Time",
  "audit.ip": "IP",
  "audit.range": "Range",
  "audit.range.24h": "24 hours",
  "audit.range.7d": "7 days",
  "audit.range.30d": "30 days",
  "audit.range.all": "All",
  "audit.actorTypeCol": "Actor type",
  "audit.actorType.user": "User",
  "audit.actorType.api_key": "API key",
  "audit.actorType.agent": "Resource",
  "audit.actorType.system": "System",
  "audit.targetType": "Target type",
  "audit.allInGroup": "{prefix}.* — all",
  "audit.emptyDesc": "Nothing auditable has happened on this server yet.",
  "audit.noPayload": "This entry carries no payload",
  "audit.exportTooWide": "The range is too wide; narrow it and try again.",
  "settings.saved": "Settings saved",
  "settings.noChanges": "Nothing has changed",
  "settings.frameworkNote": "Changing the framework also rewrites config.general.framework and produces a new config version.",
  "settings.timezoneNote": "Dates are rendered in this time zone.",
  "settings.contributeNote": "Your confirmed bans are proposed to the global list.",
  "settings.globalBansMode": "Global ban mode",
  "settings.changeInConfig": "Change it in the configuration",
  "settings.detectionsRetentionDays": "Detection retention",
  "settings.screenshotsRetentionDays": "Screenshot retention",
  "settings.installNote": "The key is masked here; the full key is only ever shown once, when it is rotated.",
  "settings.deleted": "{name} deleted",

  // ── staff pages: dashboard, live, detections, players, lookup ──
  "dashboard.activeBans": "Active bans: {n}",
  "dashboard.agentIp": "Resource IP",
  "dashboard.agentVersion": "Resource version",
  "dashboard.banListVersion": "Ban list version",
  "dashboard.configVersion": "Configuration",
  "dashboard.lastHeartbeat": "Last heartbeat",
  "dashboard.peakPlayers": "Peak {n} players",
  "dashboard.resourceCount": "Resource count",
  "dashboard.trendHint": "Trend within the selected range",
  "detection.action": "Action",
  "detection.batchId": "Batch id",
  "detection.bulkDone": "{updated} updated, {skipped} skipped",
  "detection.bulkLimit": "At most {n} rows can be reviewed at once",
  "detection.clockSkew": "Clock skew",
  "detection.eventId": "Event id",
  "detection.falsePositive": "False positive",
  "detection.key": "Module",
  "detection.markResolved": "Mark resolved",
  "detection.notExecuted": "Action was not carried out",
  "detection.otherModules": "Other ({n})",
  "detection.player": "Player",
  "detection.rangeToday": "Today",
  "detection.resolved": "Resolved",
  "detection.reviewSaved": "Review saved",
  "detection.reviewed": "Review",
  "detection.reviewedAtHint": "This row has already been reviewed",
  "detection.searchPlaceholder": "Player name or identifier",
  "detection.selectedCount": "{n} rows selected",
  "detection.severity": "Severity",
  "detection.side": "Side",
  "detection.total": "Total",
  "filter.dateRange": "Date range",
  "player.actions": "Actions",
  "player.bans": "Bans",
  "player.detailTitle": "Player detail",
  "player.detections": "Detections",
  "player.identifiers": "Identifiers",
  "player.lastSource": "Server id",
  "player.lookupHelp": "Paste an identifier; it is searched across every server you belong to",
  "player.lookupInvalid": "That identifier was not recognised",
  "player.lookupRetryIn": "Try again in {n} seconds",
  "player.name": "Player",
  "player.noOtherServers": "Not seen on any other server",
  "player.noSessions": "No sessions recorded",
  "player.notConnected": "The player is not connected right now",
  "player.notesAutosave": "Saved automatically when you stop typing",
  "player.searchPlaceholder": "Name or identifier",
  "player.sessionOngoing": "Ongoing",
  "player.sort": "Sort",
  "player.whitelisted": "Whitelisted",
  "screenshot.bytes": "Size",
  "screenshot.expiresAt": "Deleted at",
  "screenshot.sha256": "SHA-256",
  // ── marketing: the public landing and pricing pages ──
  "marketing.nav": "Main navigation",
  "marketing.nav.features": "Features",
  "marketing.nav.how": "How it works",
  "marketing.nav.pricing": "Pricing",
  "marketing.nav.faq": "FAQ",
  "marketing.cta.start": "Start free",
  "marketing.cta.panel": "Open panel",
  "marketing.hero.badge": "Cloud-managed anticheat for FiveM",
  "marketing.hero.title": "Protect your server, see the evidence, ban nobody unfairly",
  "marketing.hero.lead": "SentinelAC is a FiveM anticheat run from one panel. It lives inside every protected resource's own Lua state, keeps the evidence behind every detection, and never bans on a claim your server could not confirm.",
  "marketing.hero.note": "Six lines in server.cfg. No card required.",
  "marketing.stat.modules": "detection modules",
  "marketing.stat.sides": "client · server · cloud",
  "marketing.stat.sidesValue": "3 layers",
  "marketing.stat.latency": "live feed latency",
  "marketing.stat.latencyValue": "< 1 s",
  "marketing.stat.lang": "panel languages",
  "marketing.stat.langValue": "TR / EN",
  "marketing.features.eyebrow": "What it does",
  "marketing.features.title": "Everything you expect from an anticheat, plus the evidence",
  "marketing.features.lead": "Every module carries its own action, strike threshold, window and evidence envelope. None of it is switch-on-and-hope.",
  "marketing.feature.modules.title": "Layered detection",
  "marketing.feature.modules.body": "Godmode, noclip, teleport, weapons, vehicles, explosions, entity spam and more — on the client, the server and in the cloud.",
  "marketing.feature.combat.title": "Combat layer",
  "marketing.feature.combat.body": "Silent aim, no recoil, rate of fire, magic bullet and a server-side headshot distribution.",
  "marketing.feature.map.title": "Live map",
  "marketing.feature.map.body": "See where players are, right now. A player who trips a detection is marked on the map; click through to their file.",
  "marketing.feature.rules.title": "Event rules",
  "marketing.feature.rules.body": "Write rules over your own net events: name matches, argument conditions, a per-player rate limit.",
  "marketing.feature.global.title": "Global ban network",
  "marketing.feature.global.body": "Confirmed bans are proposed to other servers. Proposers carry a reputation score, so the list stays clean.",
  "marketing.feature.evidence.title": "Evidence and screenshots",
  "marketing.feature.evidence.body": "Every detection arrives with samples, a strike count and an optional screenshot. When someone appeals, you have something.",
  "marketing.feature.discord.title": "Discord integration",
  "marketing.feature.discord.body": "A webhook per category: connections, bans, detections, screenshots.",
  "marketing.feature.frameworks.title": "Framework aware",
  "marketing.feature.frameworks.body": "ESX, QBCore, Qbox and standalone. Inventory checks ask your framework, not a guess.",
  "marketing.how.eyebrow": "Setup",
  "marketing.how.title": "Three steps, ten minutes",
  "marketing.how.lead": "Download the resource, add the lines to server.cfg, open the panel.",
  "marketing.how.cfgLabel": "server.cfg",
  "marketing.step.install.title": "Install",
  "marketing.step.install.body": "Download the resource from the panel, extract it into resources, put your license key in server.cfg.",
  "marketing.step.watch.title": "Watch",
  "marketing.step.watch.body": "Spend the first day in log-only mode. Nobody is kicked; you see what would have been caught in the live feed.",
  "marketing.step.promote.title": "Promote",
  "marketing.step.promote.body": "Once you know what normal looks like on your server, move modules up to kick and ban one at a time.",
  "marketing.honesty.title": "What we do not claim",
  "marketing.honesty.body1": "External aimbots — Eulen, RedEngine and the rest — are drawn by a DirectX hook outside the script runtime. No Lua native can observe one. Not ours, not our competitors'. Everything sold as \"aimbot detection\", including ours, is a behavioural heuristic.",
  "marketing.honesty.body2": "So our combat modules measure the consequence a human cannot produce rather than the cheat itself: a bullet landing where the crosshair never pointed, a rate of fire above the weapon's mechanical limit, a headshot distribution nobody sustains. And that is why none of them can propose a global ban.",
  "marketing.honesty.body3": "A claim the server can verify but did not downgrades from ban to kick. Statistical modules ship as log-only. We would rather you measure a threshold than inflict one.",
  "marketing.pricing.eyebrow": "Pricing",
  "marketing.pricing.title": "By server count, with no surprises",
  "marketing.pricing.lead": "The full detection catalog is on every plan. Plans differ on server count, retention and team size.",
  "marketing.plan.popular": "Most chosen",
  "marketing.plan.free": "Free",
  "marketing.plan.custom": "Let's talk",
  "marketing.plan.perMonth": "/mo",
  "marketing.plan.contact": "Contact us",
  "marketing.plan.starter.name": "Starter",
  "marketing.plan.starter.for": "For a new community running one server.",
  "marketing.plan.pro.name": "Pro",
  "marketing.plan.pro.for": "For a populated server run by a team.",
  "marketing.plan.enterprise.name": "Enterprise",
  "marketing.plan.enterprise.for": "For networks of servers that need an SLA.",
  "marketing.limit.unlimited": "Unlimited",
  "marketing.limit.servers": "{n} servers",
  "marketing.limit.retention": "{n} days of history",
  "marketing.limit.seats": "{n} team members",
  "marketing.limit.days": "{n} days",
  "marketing.limit.screenshots": "Screenshot at detection",
  "marketing.limit.liveMap": "Live player map",
  "marketing.limit.eventRules": "Event rules",
  "marketing.limit.globalBans": "Global ban network",
  "marketing.limit.discord": "Discord webhooks",
  "marketing.limit.apiKeys": "REST API keys",
  "marketing.compare.title": "Compare the plans",
  "marketing.compare.feature": "Feature",
  "marketing.compare.row.servers": "Servers",
  "marketing.compare.row.retentionDays": "History kept",
  "marketing.compare.row.seats": "Team members",
  "marketing.compare.row.modules": "The full detection catalog",
  "marketing.compare.row.logOnly": "Log-only mode",
  "marketing.compare.row.serverConfirm": "No ban without server confirmation",
  "marketing.compare.row.screenshots": "Screenshots",
  "marketing.compare.row.liveMap": "Live map",
  "marketing.compare.row.eventRules": "Event rules",
  "marketing.compare.row.globalBans": "Global ban network",
  "marketing.compare.row.discord": "Discord webhooks",
  "marketing.compare.row.apiKeys": "API keys",
  "marketing.compare.row.support": "Support",
  "marketing.support.starter": "Community",
  "marketing.support.pro": "Priority",
  "marketing.support.enterprise": "Private channel + SLA",
  "marketing.billing.title": "Billing and subscription",
  "marketing.billing.trial.q": "Do I have to pay to try it?",
  "marketing.billing.trial.a": "No. Starter is free forever and carries the full detection catalog; the limits are on server count, retention and team size.",
  "marketing.billing.change.q": "Can I change plan?",
  "marketing.billing.change.a": "Yes, from the panel, whenever you like. An upgrade applies at once; a downgrade at the end of the current period.",
  "marketing.billing.cancel.q": "What happens to my data if I cancel?",
  "marketing.billing.cancel.a": "Your account drops to Starter and retention shortens to that plan. Your bans and server config stop; they are not deleted.",
  "marketing.billing.invoice.q": "Can I get an invoice?",
  "marketing.billing.invoice.a": "Yes. Every payment has an invoice you can download from the Plan page.",
  "marketing.billing.currency.q": "Why euros?",
  "marketing.billing.currency.a": "FiveM hosting and script sales are priced in euros, so comparing us to anything else is easier this way.",
  "marketing.contact.title": "Not sure which one?",
  "marketing.contact.body": "Tell us about your server and we will work out which plan covers it. Enterprise can include custom modules and an SLA.",
  "marketing.faq.eyebrow": "FAQ",
  "marketing.faq.title": "What people ask before installing",
  "marketing.faq.aimbot.q": "Does it actually catch aimbots?",
  "marketing.faq.aimbot.a": "An external aimbot cannot be seen from Lua — that is the platform's limit, not ours. What we catch is the consequence: a bullet landing where the crosshair never pointed, a rate of fire above the mechanical limit, a run of shots with no recoil, and the headshot distribution on the server side. These ship as kick and log, not ban.",
  "marketing.faq.performance.q": "Will it slow my server down?",
  "marketing.faq.performance.a": "The client runs one scheduler thread; modules tick on their own period and the idle cost is below measurement. The modules that do per-frame work only wake while the player is shooting.",
  "marketing.faq.falsePositives.q": "What about false positives?",
  "marketing.faq.falsePositives.a": "Spend the first day in log-only mode. Every module has a tunable strike threshold and window, a whitelist and a bypass export, and a claim the server cannot confirm can never become a ban.",
  "marketing.faq.offline.q": "What if the cloud is unreachable?",
  "marketing.faq.offline.a": "The resource keeps the last verified config and ban list on disk. You choose what happens to joins while it is offline: cache, allow or deny.",
  "marketing.faq.selfHost.q": "Can I self-host it?",
  "marketing.faq.selfHost.a": "On Enterprise, yes. The installer is written to go onto a shared machine without disturbing the other sites already on it.",
  "marketing.faq.data.q": "What data do you keep?",
  "marketing.faq.data.a": "Player identifiers, detection evidence and optional screenshots. IP addresses can be hidden in logs. Retention is enforced by an hourly job according to your plan.",
  "marketing.final.title": "Install it tonight, read what it caught in the morning",
  "marketing.final.body": "Start in log-only mode. None of your players are affected; you simply find out what is actually happening on your server.",
  "marketing.footer.tagline": "Cloud-managed anticheat for FiveM servers.",

  // ── the live map ──
  "nav.map": "Live map",
  "map.title": "Live map",
  "map.live": "Live",
  "map.stale": "No data",
  "map.online": "{n} players",
  "map.reset": "Reset view",
  "map.roster": "Players",
  "map.emptyTitle": "Nobody here",
  "map.emptyBody": "This stays empty while the server is offline or the live map is switched off.",
  "map.privacy": "Positions are stored nowhere: they arrive from your server, are drawn here and forgotten. You can switch the map off entirely under Config > General.",

  "config.sectionLiveMap": "Live map",
  "config.sectionLiveMapDesc": "Player positions are read from your server, forwarded to the panel and stored nowhere. Switching it off stops the traffic entirely.",
  "config.field.liveMap.enabled": "Enable the live map",
  "config.field.liveMap.enabledDesc": "While off the server sends no positions and the map page stays empty.",
  "config.field.liveMap.intervalMs": "Post interval (ms)",
  "config.field.liveMap.intervalMsDesc": "Lower is smoother, and costs more server time and bandwidth.",

  // ── the customer plan page ──
  "nav.plan": "Plan",
  "plan.title": "Your plan",
  "plan.current": "Current plan",
  "plan.usageTitle": "Usage",
  "plan.usageDesc": "The limits we can actually measure. We do not draw a bar for something we cannot count.",
  "plan.includedTitle": "Included in your plan",
  "plan.changeTitle": "Change plan",
  "plan.changeDesc": "Moving up is for when your server count or your team has outgrown the current one.",
  "plan.contactNote": "No payment provider is connected yet, so a plan change is a conversation for now. We would rather say that than show you a half-built card form.",
  "plan.comparePlans": "Compare plans",

  "map.tilt": "3D view",
  "map.flat": "Flat view",
  "map.legend.onFoot": "On foot",
  "map.legend.inVehicle": "Driving",
  "map.legend.hurt": "Hurt",
  "map.legend.down": "Down",
  "config.field.liveMap.imageUrl": "Map image (optional)",
  "config.field.liveMap.imageUrlDesc": "Left empty, the panel draws its own San Andreas map. Give the https address of a map image you hold the right to use and the player dots are drawn over it instead.",

  "marketing.mock.feed": "Live feed",
  "marketing.mock.module": "Module settings",
  "marketing.mock.map": "Live map",
  "marketing.mock.strikes": "strikes",

  "marketing.proof.tuning.eyebrow": "Tuning",
  "marketing.proof.tuning.title": "Every module has its own action, threshold and window",
  "marketing.proof.tuning.body": "You do not switch a module on and hope. You choose what it does (log, kick, ban), how many samples it takes to trip, the window it counts them in, and whether it asks for a screenshot. Whitelists and a bypass export are per module too.",
  "marketing.proof.map.eyebrow": "Live map",
  "marketing.proof.map.title": "See who is where, right now",
  "marketing.proof.map.body": "Players move on the map live, coloured by on foot, driving, hurt or down. Click one in the list and the map goes to them. Positions are stored nowhere — they arrive from your server, are drawn, and are forgotten.",
  "marketing.honesty.eyebrow": "Candour",
  "marketing.honesty.cannotTitle": "What we cannot do",
  "marketing.honesty.canTitle": "What we measure instead",
  "marketing.honesty.cannot.external": "We cannot see an external aimbot or ESP. They are drawn by a DirectX hook outside the script runtime, where no Lua native reaches.",
  "marketing.honesty.cannot.memory": "We cannot directly see cheats that call no native at all, such as a pure memory noclip.",
  "marketing.honesty.cannot.certainty": "We cannot turn a behavioural heuristic into certainty. That is why no combat module can propose a global ban.",
  "marketing.honesty.can.divergence": "A bullet landing off the axis the camera was pointing down — the one trace of silent aim that Lua can see.",
  "marketing.honesty.can.cadence": "Fire faster than the weapon can cycle, and runs of shots with no recoil. Neither has a skill component.",
  "marketing.honesty.can.distribution": "The headshot distribution, server-side: over a large enough sample, a ratio nobody sustains.",
  "marketing.honesty.can.server": "Everything the server can verify for itself — damage events, position, invincibility — whatever the client claims.",

  "map.online.one": "1 player",
  "map.online.other": "{n} players",
  "map.lastSeen": "{n} players, {s}s ago",

  "map.connecting": "Connecting",

  "plan.notMeasured": "This is your plan's cap, not your current usage. We do not count team members yet.",

  "map.image.title": "Use the real GTA map",
  "map.image.body": "The panel is drawing its own San Andreas. Give the address of a GTA map image you hold the right to use and the player dots are drawn over it instead.",
  "map.image.hint": "Must start with https. The world rectangle the image covers is set under Config > General; the defaults match a standard FiveM minimap export.",
  "map.image.saved": "Map image saved",
  "map.image.failed": "Could not save the map image",

}
